import urls from "global/constants/UrlConstants";
import { DateRange, SortingConfig } from "models/interfaces";
import { getCallParams, makeCall } from "utils/service";

// Both endpoints declare @RequestBody EntitySearchSorter on the backend, so
// they require a JSON body — even when nothing is filtered. `dateRange` is
// only included when both ends are set; a partial range is dropped because
// the backend expects `{ fromDate, toDate }` together or not at all.
const buildFolderSearchBody = (
  searchText?: string,
  sortConfig?: SortingConfig[],
  dateRange?: DateRange,
) => {
  const body: Record<string, unknown> = {
    searchText: searchText ?? "",
    entitySorters: sortConfig ?? [],
  };
  if (dateRange?.fromDate && dateRange?.toDate) {
    body.dateRange = {
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    };
  }
  return body;
};

export const fetchSignFolderData = async (
  pageNumber: number,
  parentId: number,
  status: string,
  sortConfig: SortingConfig[],
  searchText: string = "",
  dateRange?: DateRange,
) => {
  const url = `${urls.SIGN_FOLDER_GET_PAGE}/${pageNumber}/status/${status}/parent/${parentId}`;
  try {
    const callParams = await getCallParams(
      "POST",
      buildFolderSearchBody(searchText, sortConfig, dateRange),
    );
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSignFolderDataCount = async (
  parentId: number,
  status: string,
  sortConfig?: SortingConfig[],
  searchText: string = "",
  dateRange?: DateRange,
) => {
  const url = `${urls.SIGN_FOLDER_GET_COUNT}/status/${status}/parent/${parentId}`;
  try {
    const callParams = await getCallParams(
      "POST",
      buildFolderSearchBody(searchText, sortConfig, dateRange),
    );
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSignFolderTree = async () => {
  const url = `${urls.SIGN_FOLDER_GET_TREE}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const createSignFolder = async (
  templateName: string,
  parentId: number,
) => {
  const url = `${urls.SIGN_FOLDER_CREATE}`;
  try {
    const callParams = await getCallParams("POST", {
      type: "folder",
      templateName,
      parentId,
    });
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const moveSignFolder = async (id: number, destinationFolder: number) => {
  const url = `${urls.SIGN_FOLDER_MOVE}/${id}/${destinationFolder}`;
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const renameSignFolder = async (id: number, name: string) => {
  const url = `${urls.SIGN_FOLDER_RENAME}/${id}/${encodeURIComponent(name)}`;
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const deleteSignFolderSafe = async (id: number) => {
  const url = `${urls.SIGN_FOLDER_DELETE_SAFE}/${id}`;
  try {
    const callParams = await getCallParams("DELETE");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const deleteSignFolderAnyway = async (id: number) => {
  const url = `${urls.SIGN_FOLDER_DELETE_ANYWAY}/${id}`;
  try {
    const callParams = await getCallParams("DELETE");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const searchSignRequestsAndFolders = async (
  searchText: string,
  signal?: AbortSignal,
) => {
  const url = `${urls.SIGN_FOLDER_SEARCH}`;
  const sortConfig: SortingConfig[] = [
    { entity: "signRequest", order: "DESC", field: "createdOn" },
  ];
  const callParams = await getCallParams(
    "POST",
    buildFolderSearchBody(searchText, sortConfig),
    signal,
  );
  return await makeCall(url, callParams);
};
